/* istanbul ignore file */ /* plugin extension code */

import { useCallback, useState } from 'react';
import { useNoticeBannersStackContext } from '@sweetgreen/garnish';

import type { RegisterInput, RegisterMutation } from '@order/graphql';
import { useRegisterMutation } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';
import { getUrqlError } from '@order/utils';

export const useSignUp = () => {
  const { t, formatDate } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  const [signUpResponseData, setSignUpResponseData] = useState<
    RegisterMutation['register'] | null
  >(null);
  const [signUpResponse, signUpMutation] = useRegisterMutation();
  const [userSignUpVerificationEmail, setUserSignUpVerificationEmail] =
    useState('');

  const isSigningUp = signUpResponse?.fetching;
  const shouldVerifyUser = signUpResponseData?.__typename === 'RegisterSuccess';

  // ─────────────────────── Telemetry ──────────────────────────────────
  const { track } = useTelemetry();
  const onSuccessfulSignup = useCallback(
    (input: RegisterInput) => {
      track('join.success', {
        name: `${input.firstName} ${input.lastName}`,
        email: input.email,
        date: formatDate(new Date()),
        referred: false,
      });
    },
    [formatDate, track],
  );
  const onFailedSignup = useCallback(
    (userError?: string, systemError?: string) => {
      track('join.failure', { userError, systemError });
    },
    [track],
  );

  // ────────────────────────────────────────────────────────────────────

  const signUp = useCallback(
    async (input: RegisterInput) => {
      const response = await signUpMutation({ input });
      const data = response?.data?.register;

      const hasValidationErrors = data?.__typename === 'ValidationError';
      const isSuccessfullySignedUp = data?.__typename === 'RegisterSuccess';

      if (!isSuccessfullySignedUp || response.error) {
        const systemError = getUrqlError(response.error);
        const validationError = hasValidationErrors
          ? data.fieldErrors?.[0]?.message
          : undefined;
        const userError = validationError ?? t('join.error');

        addNoticeBanner({
          text: userError,
          palette: 'caution',
        });
        onFailedSignup(userError, systemError);

        return data;
      }

      onSuccessfulSignup(input);
      setUserSignUpVerificationEmail(input.email);
      setSignUpResponseData(data);

      return data;
    },
    [signUpMutation, addNoticeBanner, onSuccessfulSignup, onFailedSignup, t],
  );

  const resetSignUpData = useCallback(() => {
    setSignUpResponseData(null);
  }, []);

  // ────────────────────────────────────────────────────────────────────

  return {
    resetSignUpData,
    signUp,
    isSigningUp,
    userSignUpVerificationEmail,
    shouldVerifyUser,
  };
};
